import { PlusCircle, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";
import {
  useCancelStockOpname,
  useCreateStockOpname,
  useStockOpnames,
} from "@/hooks/use-stock-opnames";
import { newIdempotencyKey } from "@/lib/idempotency";

import { StockOpnameCancelDialog } from "./stock-opname-delete-dialog";
import { StockOpnameNewSessionDialog } from "./stock-opname-new-session-dialog";
import { StockOpnameSummaryCards } from "./stock-opname-summary-cards";
import { StockOpnameTableCard } from "./stock-opname-table-card";

import type {
  StockOpname,
  StockOpnameStatus,
  StockOpnameType,
} from "@artisancode/api-types";

interface Props {
  type?: StockOpnameType;
}

export function StockOpnameList({ type = "raw_material" }: Props) {
  const navigate = useNavigate();
  const isRawMaterial = type === "raw_material";
  const sequenceModuleCode = isRawMaterial
    ? "stock_opname_raw_material"
    : "stock_opname_product";
  const { preview: sequencePreview } = useNextNumberPreview(sequenceModuleCode);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // New Opname Modal state
  const [newModalOpen, setNewModalOpen] = useState(false);
  // One key per opened "new session" dialog: a retry after a network error reuses
  // it, so the server returns the opname it already created instead of a second.
  const idempotencyKey = useMemo(
    () => newIdempotencyKey("sopn"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newModalOpen],
  );
  const [newNotes, setNewNotes] = useState("");

  // Cancel confirmation
  const [cancellingOpname, setCancellingOpname] = useState<StockOpname | null>(
    null,
  );

  const {
    data: opnamesRes,
    isLoading,
    refetch,
  } = useStockOpnames({
    type,
    search: searchQuery || undefined,
    status:
      statusFilter === "all" ? undefined : (statusFilter as StockOpnameStatus),
    per_page: 100,
  });
  const opnames = opnamesRes?.items ?? [];

  const createMutation = useCreateStockOpname();
  const cancelMutation = useCancelStockOpname();

  // Summary Metrics
  const totalSessions = opnames.length;
  const completedSessions = opnames.filter(
    (o) => o.status === "completed",
  ).length;
  const draftSessions = opnames.filter((o) => o.status === "draft").length;
  const totalDiscrepancies = opnames.reduce(
    (sum, o) => sum + (o.discrepancyCount || 0),
    0,
  );

  function handleOpenOpname(opname: StockOpname) {
    navigate(`/stock-opname/sessions/${opname.id}`);
  }

  function handleCreateConfirm() {
    createMutation.mutate(
      { body: { type, note: newNotes.trim() || undefined }, idempotencyKey },
      {
        onSuccess: (newOpname) => {
          setNewModalOpen(false);
          setNewNotes("");
          toast.success(`Sesi opname ${newOpname.code} berhasil dibuat.`);
          // Go straight to counting
          handleOpenOpname(newOpname);
        },
      },
    );
  }

  async function handleCancelConfirm(reason: string) {
    if (!cancellingOpname) return;
    await cancelMutation.mutateAsync({
      id: cancellingOpname.id,
      cancelReason: reason,
    });
    toast.success("Sesi stock opname berhasil dibatalkan.");
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isRawMaterial ? "Opname Bahan Baku" : "Opname Produk"}
        description={
          isRawMaterial
            ? "Pencatatan dan rekonsiliasi fisik stok bahan baku utuh di toko dan kemasan terbuka di bar."
            : "Pencatatan dan rekonsiliasi fisik stok produk jadi siap jual."
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="min-h-[40px] sm:min-h-0 sm:h-9 gap-1.5 text-xs"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`}
              />
              Muat Ulang
            </Button>

            <Button
              size="sm"
              onClick={() => setNewModalOpen(true)}
              className="min-h-[40px] sm:min-h-0 sm:h-9 gap-1.5 text-xs shadow-xs"
            >
              <PlusCircle className="h-4 w-4" />
              Mulai Sesi Opname
            </Button>
          </div>
        }
      />

      {/* Summary KPI Cards */}
      <StockOpnameSummaryCards
        totalSessions={totalSessions}
        completedSessions={completedSessions}
        draftSessions={draftSessions}
        totalDiscrepancies={totalDiscrepancies}
      />

      {/* Table Card */}
      <StockOpnameTableCard
        opnames={opnames}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onOpenOpname={handleOpenOpname}
        onCancelRequest={(opname) => setCancellingOpname(opname)}
        isRawMaterial={isRawMaterial}
      />

      {/* New Session Dialog */}
      <StockOpnameNewSessionDialog
        open={newModalOpen}
        onOpenChange={setNewModalOpen}
        type={type}
        sequencePreview={sequencePreview}
        notes={newNotes}
        onNotesChange={setNewNotes}
        onConfirm={handleCreateConfirm}
        isSubmitting={createMutation.isPending}
      />

      {/* Cancel Session Dialog */}
      <StockOpnameCancelDialog
        opname={cancellingOpname}
        onOpenChange={(open) => {
          if (!open) setCancellingOpname(null);
        }}
        onConfirm={handleCancelConfirm}
      />
    </div>
  );
}
